import {
  DELETED_PERSON,
  type ReportListItem,
  type ReportPerson,
  type ReportVersionOrigin,
} from '@ni/shared';

export { DELETED_PERSON } from '@ni/shared';

/**
 * Reports in the office's words (docs/notes/2026-09-22-reports-design.md §2).
 * No tokens reach the page: every origin, status and reading kind is named here.
 */

export const REPORTS_TITLE = 'Reports';

export const ORIGIN_LABELS: Readonly<Record<ReportVersionOrigin, string>> = {
  generate: 'First version',
  update: 'Inputs changed',
  refresh: 'Refreshed',
  revise: 'Revised',
  edit: 'Edited',
};

export const STATUS_LABELS: Readonly<Record<ReportListItem['status'], string>> = {
  preparing: 'Being prepared',
  ready: 'Ready',
  failed: 'Not prepared',
};

export function personName(person: ReportPerson): string {
  return person.deleted || person.display_name === null ? DELETED_PERSON : person.display_name;
}

/** "Ana Ruiz, Mark Chen and 4 others" — a list row's people, short. */
export function peopleLine(people: readonly ReportPerson[], shown = 3): string {
  const names = people.map(personName);
  if (names.length <= shown) {
    return names.length <= 1
      ? (names[0] ?? '')
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }
  const rest = names.length - shown;
  return `${names.slice(0, shown).join(', ')} and ${rest} ${rest === 1 ? 'other' : 'others'}`;
}

/**
 * Upcoming first, soonest first; then undated; then past, most recent first.
 * An event that has happened is still findable, just not in the way.
 */
export function sortReports(items: readonly ReportListItem[], today: string): ReportListItem[] {
  const rank = (item: ReportListItem) =>
    item.for_date === null ? 1 : item.for_date >= today ? 0 : 2;
  return [...items].sort((a, b) => {
    const byRank = rank(a) - rank(b);
    if (byRank !== 0) return byRank;
    if (a.for_date && b.for_date) {
      return rank(a) === 0
        ? a.for_date.localeCompare(b.for_date)
        : b.for_date.localeCompare(a.for_date);
    }
    return b.created_at.localeCompare(a.created_at);
  });
}

/**
 * The report panel's line for an attendee a check raised who is not on the
 * record: which check and tier, in its own words ("Signs of disruption:
 * Flagged"), or the older wording for a version written before it was kept.
 */
export function reportFlaggedOffRecord(name: string, why: string | null): string {
  const lead = why ? `${name} (${why}) is not on record.` : `${name} is flagged and not on record.`;
  return `${lead} Research needs them on record first. `;
}
